import type {
  AssetManagementType,
  AssetStatus,
  FixedAssetIncDecSummary,
  IncDecHistoryProcessing,
  IsThisInclude,
  SecondhandAsset,
} from '@libs/enums';
import type { ItemAttributes } from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  DateRange,
  type DateRangeObject,
  ItemName,
  type ItemNames,
  Locale,
  type OrEmpty,
  ParentBranch,
  type ParentBranchObject,
  SingleDate,
  SingleValue,
  ValueSet,
  WithBlankSearch,
  type WithBlankSearchObject,
  WithExclusion,
  type WithExclusionObject,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  /**
   * 資産番号
   */
  assetNo: ParentBranchObject;
  /**
   * 資産管理区分
   */
  assetManagementClass: Set<AssetManagementType>;
  /**
   *資産番号・親範囲
   */
  assetNoParentRangeCondition: WithExclusionObject;
  /**
   * 資産状態区分
   */
  assetStateClass: Set<AssetStatus>;
  /**
   * 取得年月日
   */
  acquisitionDataCondition: DateRangeObject;
  /**
   * 資産番号(抽出条件)
   */
  assetNoRange: string;
  /**
   * 資産番号・親単位で集計する
   */
  hasTotalByAssetParentNoUnits: boolean;
  /**
   * 資産名称
   */
  assetName: string;
  /**
   * 資産予備名称1
   */
  assetSpareName01: string;
  /**
   * 資産予備名称2
   */
  assetSpareName02: string;
  /**
   * 取得年月日
   */
  acquisitionDate: Date | null;
  /**
   * 資産Id
   */
  assetId: number | null;
  /**
   * 合併受入資産(抽出条件)
   */
  acquiredMergerAssetCondition: IsThisInclude;
  /**
   * 更新中資産(抽出条件)
   */
  updatingAssetCondition: IsThisInclude;
  /**
   *資産番号・親(抽出条件)
   */
  assetNoParentCondition: string;
  /**
   *中古資産(抽出条件)
   */
  secondhandAssetCondition: IsThisInclude;
  /**
   * 除売却年月日(抽出条件)
   */
  disposalAndSalesDateCondition: DateRangeObject;
  /**
   * 移動年月日(抽出条件)
   */
  transferDataCondition: DateRangeObject;
  /**
   * 資産予備名称1(抽出条件)
   */
  assetSpareNameOneCondition: WithBlankSearchObject;
  /**
   * 資産予備名称2(抽出条件)
   */
  assetSpareNameTwoCondition: WithBlankSearchObject;
  /**
   * 固定資産増減合計表の帳票種類
   */
  fixedAssetIncreDecreTotalTableReportType: FixedAssetIncDecSummary;
  /**
   * 資産番号（グリッド用）
   */
  assetNoGrid: string;
  /**
   * 資産番号・親（グリッド用）
   */
  assetNoParentGrid: string;
  /**
   * 資産番号・枝（グリッド用）
   */
  assetNoBranchGrid: string;
  /**
   * 資産状態区分（グリッド用）
   */
  assetStateClassGrid: AssetStatus;
  /**
   *中古資産（グリッド用）
   */
  secondhandAssetGrid: OrEmpty<SecondhandAsset>;
  /**
   * 除却年月日
   */
  disposalDate: Date | null;
  /**
   * 売却年月日
   */
  salesDate: Date | null;
  /**
   * 最新移動年月日
   */
  latestTransferDate: Date | null;
  /**
   * 合併受入資産
   */
  acquiredMergerAsset: string;
  /**
   * 適用期間(FROM)
   */
  applicationPeriodFrom: string;
  /**
   * 適用期間(TO)
   */
  applicationPeriodTo: string;
  /**
   * 割増償却月数
   */
  extraDepreciationMonths: string;
  /**
   * 原始取得年月日
   */
  originalAcquisitionDate: Date | null;
  /**
   * 増減履歴処理区分
   */
  increDecreHistoryProcessClass: OrEmpty<IncDecHistoryProcessing>;
}

const attributes = {
  assetNo: ParentBranch.of(
    Characters.preset.alphanumericals({ minLen: 8, maxLen: 8, required: true }),
    Characters.preset.numerical({ minLen: 3, maxLen: 3 }),
  ),
  assetManagementClass: ValueSet.new(),
  assetNoParentRangeCondition: WithExclusion.of(Characters.preset.alphanumericalsRange({ maxLen: 250 })),
  assetStateClass: ValueSet.new(),
  acquisitionDataCondition: DateRange.ymd(),
  assetNoRange: Characters.preset.halfWidth({ maxLen: 99999 }),
  hasTotalByAssetParentNoUnits: Bool.new(),
  assetName: Characters.preset.freeFormat({ maxLen: 100 }),
  assetSpareName01: Characters.preset.freeFormat({ maxLen: 100 }),
  assetSpareName02: Characters.preset.freeFormat({ maxLen: 100 }),
  acquisitionDate: SingleDate.ymd(),
  acquiredMergerAssetCondition: SingleValue.shortValue(),
  updatingAssetCondition: SingleValue.shortValue(),
  assetNoParentCondition: Characters.preset.freeFormat({ maxLen: 15 }),
  secondhandAssetCondition: SingleValue.shortValue(),
  disposalAndSalesDateCondition: DateRange.ymd(),
  transferDataCondition: DateRange.ymd(),
  assetSpareNameOneCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 100 })),
  assetSpareNameTwoCondition: WithBlankSearch.of(Characters.preset.freeFormat({ maxLen: 100 })),
  fixedAssetIncreDecreTotalTableReportType: SingleValue.shortValue(),
  assetNoGrid: Characters.preset.freeFormat(),
  assetNoParentGrid: Characters.preset.freeFormat(),
  assetNoBranchGrid: Characters.preset.freeFormat(),
  assetStateClassGrid: SingleValue.shortValue(),
  secondhandAssetGrid: SingleValue.shortValue(),
  disposalDate: SingleDate.ymd(),
  salesDate: SingleDate.ymd(),
  latestTransferDate: SingleDate.ymd(),
  acquiredMergerAsset: Characters.preset.freeFormat(),
  applicationPeriodFrom: Characters.preset.freeFormat(),
  applicationPeriodTo: Characters.preset.freeFormat(),
  extraDepreciationMonths: Characters.preset.freeFormat(),
  originalAcquisitionDate: SingleDate.ymd(),
  increDecreHistoryProcessClass: SingleValue.shortValue(),
} satisfies ItemAttributes<Omit<Schema, 'assetId'>>;

interface Record {
  assetId: number;
  assetNo: string;
  assetNoParent: string;
  assetNoBranch: string;
  assetManagementClass: string[];
  assetNoParentRange: string;
  isAssetNoParentRangeExclusion: boolean;
  assetStateClass: string[];
  acquisitionDataFrom: string;
  acquisitionDataTo: string;
  assetNoRange: string;
  hasTotalByAssetParentNoUnits: boolean;
  assetName: string;
  assetSpareName01: string;
  assetSpareName02: string;
  acquisitionDate: string;
  acquiredMergerAssetCondition: string;
  updatingAssetCondition: string;
  assetNoParentCondition: string;
  secondhandAsset: string;
  disposalAndSalesDateFrom: string;
  disposalAndSalesDateTo: string;
  transferDataFrom: string;
  transferDataTo: string;
  isAssetSpareNameOneBlankOnly: boolean;
  isAssetSpareNameTwoBlankOnly: boolean;
  fixedAssetIncreDecreTotalTableReportType: string;
  assetStateClassGrid: string;
  disposalDate: string;
  salesDate: string;
  latestTransferDate: string;
  acquiredMergerAsset: string;
  applicationPeriodFrom: string;
  applicationPeriodTo: string;
  extraDepreciationMonths: string;
  originalAcquisitionDate: string;
  increDecreHistoryProcessClass: string;
}

const names = {
  assetId: ItemName.of([Locale.ja, '資産ID']),
  assetNo: ItemName.of([Locale.ja, '資産番号']),
  assetManagementClass: ItemName.of(
    [Locale.ja, '資産管理区分'],
    [Locale.en, 'Asset Management Classification'],
  ),
  assetNoParentRangeCondition: ItemName.of([Locale.ja, '資産番号・親範囲']),
  assetStateClass: ItemName.of([Locale.ja, '資産管理区分']),
  acquisitionDataCondition: ItemName.of([Locale.ja, '取得年月日']),
  assetNoRange: ItemName.of([Locale.ja, '資産番号']),
  hasTotalByAssetParentNoUnits: ItemName.of([Locale.ja, '資産番号・親単位で集計する']),
  assetName: ItemName.of([Locale.ja, '資産名称']),
  assetSpareName01: ItemName.of([Locale.ja, '資産予備名称1']),
  assetSpareName02: ItemName.of([Locale.ja, '資産予備名称2']),
  acquisitionDate: ItemName.of([Locale.ja, '取得年月日']),
  acquiredMergerAssetCondition: ItemName.of([Locale.ja, '合併受入資産']),
  updatingAssetCondition: ItemName.of([Locale.ja, '更新中資産']),
  assetNoParentCondition: ItemName.of([Locale.ja, '資産番号・親']),
  secondhandAssetCondition: ItemName.of([Locale.ja, '中古資産']),
  disposalAndSalesDateCondition: ItemName.of([Locale.ja, '除売却年月日']),
  transferDataCondition: ItemName.of([Locale.ja, '移動年月日']),
  assetSpareNameOneCondition: ItemName.of([Locale.ja, '資産予備名称1']),
  assetSpareNameTwoCondition: ItemName.of([Locale.ja, '資産予備名称2']),
  fixedAssetIncreDecreTotalTableReportType: ItemName.of([Locale.ja, '帳票種類']),
  assetNoGrid: ItemName.of([Locale.ja, '資産番号']),
  assetNoParentGrid: ItemName.of([Locale.ja, '資産番号・親']),
  assetNoBranchGrid: ItemName.of([Locale.ja, '資産番号・枝']),
  assetStateClassGrid: ItemName.of([Locale.ja, '資産状態区分']),
  secondhandAssetGrid: ItemName.of([Locale.ja, '中古資産区分']),
  disposalDate: ItemName.of([Locale.ja, '除却年月日']),
  salesDate: ItemName.of([Locale.ja, '売却年月日']),
  latestTransferDate: ItemName.of([Locale.ja, '最新移動年月日']),
  acquiredMergerAsset: ItemName.of([Locale.ja, '合併受入資産区分']),
  applicationPeriodFrom: ItemName.of([Locale.ja, '適用期間(FROM)']),
  applicationPeriodTo: ItemName.of([Locale.ja, '適用期間(TO)']),
  extraDepreciationMonths: ItemName.of([Locale.ja, '割増償却月数']),
  originalAcquisitionDate: ItemName.of([Locale.ja, '原始取得年月日']),
} satisfies ItemNames<Schema>;

const titleNames = {
  fixedAssetLeaderDetails: ItemName.of([Locale.ja, '固定資産台帳(1件別明細)']),
  fixedAssetDepreciationExpensesSummary: ItemName.of([Locale.ja, '固定資産償却費集計表']),
  fixedAssetImpairmentStatement: ItemName.of([Locale.ja, '固定資産減損明細表']),
  leaseLiabilityLedger: ItemName.of([Locale.ja, 'リース債務台帳']),
  fixedAssetImpairmentLossEntry: ItemName.of([Locale.ja, '固定資産減損損失計上']),
  laArticlePrincipalRepaymentSchedule: ItemName.of([Locale.ja, 'リース物件元本返済予定表']),
  fixedAssetCancelIncreDecre: ItemName.of([Locale.ja, '固定資産増減取消']),
  fixedAssetListInquiry: ItemName.of([Locale.ja, '固定資産一覧照会']),
  fixedAssetIncreDecreTotalTable: ItemName.of([Locale.ja, '固定資産増減合計表']),
  laContractPaymentConfirmationForm: ItemName.of([Locale.ja, 'リース契約支払確認表']),
};

const labelNames = {
  assetStateClass: ItemName.of([Locale.ja, '資産状態区分']),
  depreciationAssetClass: ItemName.of([Locale.ja, '償却資産区分']),
  increDecreHistoryProcessClass: ItemName.of([Locale.ja, '増減履歴処理区分']),
};

const faBasicInfo = {
  names,
  labelNames,
  titleNames,
  attributes,
};

interface FaBasicInfo {
  schema: Schema;
  record: Record;
}

export { faBasicInfo };
export type { FaBasicInfo };
